import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { State } from '@meraki-internal/state';
import { UserState } from './UserState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';

export type INotificationType = 'scheduledTips' | 'dailyLog' | 'email' | 'push';

export interface IUserSettings {
    notifications: {
        scheduledTips: {
            enabled: boolean;
        },
        dailyLog: {
            enabled: boolean;
        },
        community?: {
            pushDisabled?: boolean;
            emailDisabled?: boolean;
        }
    }
};

export class UserSettingsState extends State<IUserSettings | undefined> {
    static inject = () => [
        APIClient,
        UserState,
        MixPanelEventEmitter
    ];

    constructor(
        private apiClient: APIClient,
        private userState: UserState,
        private mixpanel: MixPanelEventEmitter
    ) {
        super(undefined);
    };

    load = async () => {
        const settings = await this.apiClient.get(this.userState.state.links?.settings);
        if (settings) {
            this.setState(settings);
        }
    };

    save = async () => {
        const settings = await this.apiClient.put(this.userState.state.links?.settings, this.state);
        this.setState(settings);
    };

    // NOTE: if we add any more, we should separate / refactor instead of 'push' / 'email' being tied to community only
    track = (type: INotificationType, value: boolean) => {
        const trackingLabels = {
            'scheduledTips': 'Tips & Inspiration Push Notifications Toggled',
            'dailyLog': 'Daily Care Journal Push Notifications Toggled',
            'push': 'Community Updates Push Notifications Toggled',
            'email': 'Community Updates Email Notifications Toggled'
        };

        this.mixpanel.track(trackingLabels[type], () => ({
            'Toggled To': value ? 'On' : 'Off'
        }));
    };

    // NOTE: if we add any more, we should separate / refactor instead of isCommunity boolean
    updateNotificationSetting = (type: INotificationType, value: boolean, isCommunity?: boolean) => {
        let notification;

        if (isCommunity) {
            notification = {
                community: {
                    ...this.state?.notifications.community,
                    ...{[`${type}Disabled`]: !value}
                }
            };
        } else {
            notification = {
                [type]: {enabled: value}
            };
        }

        this.setState({...this.state, ...{notifications: {...this.state!.notifications, ...notification}}});

        this.track(type, value);
    };

    // warning: this is invoked from NotificationsConsentDialog when state may not be loaded yet,
    // so can't use this.updateNotificationSetting()
    disableNotifications = () => {
        const allDisabled: IUserSettings = {
            notifications: {
                scheduledTips: { enabled: false },
                dailyLog: { enabled: false },
                community: {
                    pushDisabled: true,
                    emailDisabled: true
                }
            }
        };

        this.setState(allDisabled);
    };
};
